import React, { useState, useRef, lazy, Suspense, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginModal from '../../../components/LoginModal/LoginModal';
import HomeBanner from '../../../components/HomeBanner/HomeBanner';

// Log message about the logo file
// console.log('Note: Please add the actual kuchipuchi_logo.png file to the public directory for the logo to appear properly');

// Lazy-loaded section components to improve initial load performance
const Header = lazy(() => import('./sections/Header'));
const HeroSection = lazy(() => import('./sections/HeroSection'));
const QuickStoriesSection = lazy(() => import('./sections/QuickStoriesSection'));
const StorybookSection = lazy(() => import('./sections/StorybookSection'));
const PhotoBookSection = lazy(() => import('./sections/PhotoBookSection'));
const CraftedWithCareSection = lazy(() => import('./sections/CraftedWithCareSection'));
const ReadStoriesSection = lazy(() => import('./sections/ReadStoriesSection'));
const ListenStoriesSection = lazy(() => import('./sections/ListenStoriesSection'));
const TestimonialsSection = lazy(() => import('./sections/TestimonialsSection'));
const FableWhispererSection = lazy(() => import('./sections/FableWhispererSection'));
const Footer = lazy(() => import('./sections/Footer'));

const Landing = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const storiesRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      quote: "The moment my daughter saw herself as the main character, her eyes lit up! She carries the book everywhere and asks us to read it every night.",
      name: "Michael P.",
      stars: 5
    },
    {
      quote: "My son was beyond excited to see himself in a space adventure. The quality of illustrations is outstanding and the story is perfectly tailored to his interests!",
      name: "Sarah K.",
      stars: 5
    },
    {
      quote: "As a grandparent, this was the perfect gift. My granddaughter was thrilled to be the star of her own book. We read it on every video call now and she loves it!",
      name: "Robert J.",
      stars: 5
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((current) => (current + 1) % testimonials.length);
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);

  const audioBooks = [
    {
      title: "Starlight",
      description: "When young astronomer Emma discovers a magical new star, she and her friends make a wish that lights up the night sky with wonder",
      duration: "5:30",
      image: "/images/choose_your_book/book1.png",
      audioSample: "/audio/samples/1_starlight.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Rainbow Sail",
      description: "Join Sparkles the boat, Finley the fish, and Sunny the seagull on a magical adventure to the end of the rainbow and a glittering surprise",
      duration: "4:45",
      image: "/images/choose_your_book/book2.png",
      audioSample: "/audio/samples/2_rainbow-sail.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Snail Adventure",
      description: "When little Sammy the snail sets out to find a home, his garden friends and a wise turtle help him discover the perfect shell to carry on his back!",
      duration: "6:15",
      image: "/images/choose_your_book/book3.png",
      audioSample: "/audio/samples/3_snail-adventure.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Dragon Rescue",
      description: "When brave Timmy the knight meets a dragon with a broken wing, he teams up with a wizard and a fairy to bring back the dragon's joy and flight! ",
      duration: "5:15",
      image: "/images/choose_your_book/book4.png",
      audioSample: "/audio/samples/4_dragon-rescue.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Friendship Forest",
      description: "Four friends plant trees and grow a magical forest together, learning that with care and teamwork, something beautiful can blossom!",
      duration: "4:30",
      image: "/images/choose_your_book/book5.png",
      audioSample: "/audio/samples/5_friendship-forest.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Jungle Quest",
      description: "Join brave explorer Lily and her jungle friends as they unlock a hidden city filled with mystery, magic, and a sparkling treasure map to the next adventure!",
      duration: "5:45",
      image: "/images/choose_your_book/book6.png",
      audioSample: "/audio/samples/6_jungle-quest.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Little Duck",
      description: "With the help of her cheerful friends Fluffy and Chirpy, little Quackers learns to waddle proudly and quack with joy!",
      duration: "5:00",
      image: "/images/choose_your_book/book7.png",
      audioSample: "/audio/samples/7_little-duck.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Magic School",
      description: "At a magical school where kindness is the greatest magic, Lily and her enchanted friends learn a spell that fills the world with smiles!",
      duration: "6:30",
      image: "/images/choose_your_book/book8.png",
      audioSample: "/audio/samples/8_magic-school.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Moon Hopper",
      description: "Join Luna the llama and her space crew as they hop across moon craters, collect ancient moon rocks, and uncover secrets from the stars!",
      duration: "5:20",
      image: "/images/choose_your_book/book9.png",
      audioSample: "/audio/samples/9_moon-hopper.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Rainbow Adventure",
      description: "Join Felix the curious fox and his friends as they explore the magic of rainbows and uncover the science behind their colorful beauty!",
      duration: "5:30",
      image: "/images/choose_your_book/book10.png",
      audioSample: "/audio/samples/10_rainbow-adventure.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Whisper Breeze",
      description: "As the soft breeze whispers bedtime tales, Luna helps her friends, Fluffy the rabbit and Hoot the owl, drift peacefully to sleep under a starry sky.",
      duration: "5:30",
      image: "/images/choose_your_book/book11.png",
      audioSample: "/audio/samples/11_whisper-breeze.mp3",
      narrator: "KuchiPuchi"
    },
    {
      title: "Soup Harmony",
      description: "In a magical kitchen, a group of lively ingredients – carrots, potatoes, and herbs – work together to create the perfect, harmonious soup, teaching everyone the joy of teamwork and flavor!",
      duration: "5:30",
      image: "/images/choose_your_book/book12.png",
      audioSample: "/audio/samples/12_soup-harmony.mp3",
      narrator: "KuchiPuchi"
    }
  ];

  const storyBookImages = [
    '/images/storybook/story1.png',
    '/images/storybook/story2.png',
    '/images/storybook/story3.png',
    '/images/storybook/story4.png',
    '/images/storybook/story5.png',
    '/images/storybook/story6.png',
  ];

  const photoBookImages = [
    '/images/photobook/photo1.png',
    '/images/photobook/photo2.png',
    '/images/photobook/photo3.png', 
    '/images/photobook/photo4.png',
    '/images/photobook/photo5.png',
    '/images/photobook/photo6.png',
  ];

  const QuickStoriesImages = [
    '/images/quick_stories/kid1.png',
    '/images/quick_stories/kid2.png',
    '/images/quick_stories/kid3.png',
  ];

  // Sample book pages for the carousel
  const storyBooks = [
    {
      title: "Llama astronaut",
      description: "Llama goes on an adventure to the moon and meets a new friend.",
      pages: [
        "/images/stories/book1/story-image-1.jpg",
        "/images/stories/book1/story-image-2.jpg",
        "/images/stories/book1/story-image-3.jpg",
        "/images/stories/book1/story-image-4.jpg",
        "/images/stories/book1/story-image-5.jpg",
        "/images/stories/book1/story-image-6.jpg",
        "/images/stories/book1/story-image-7.jpg",
        "/images/stories/book1/story-image-8.jpg",
        "/images/stories/book1/story-image-9.jpg",
        "/images/stories/book1/story-image-10.jpg",
        "/images/stories/book1/story-image-11.jpg",        
      ]
    },
    {
      title: "The Shy Dragon",
      description: "The Shy Dragon is a story about a shy dragon who goes on an adventure and meets a new friend.",
      pages: [
        "/images/stories/book11/story-image-1.jpg",
        "/images/stories/book11/story-image-2.jpg",
        "/images/stories/book11/story-image-3.jpg",
        "/images/stories/book11/story-image-4.jpg",
        "/images/stories/book11/story-image-5.jpg",
        "/images/stories/book11/story-image-6.jpg",
        "/images/stories/book11/story-image-7.jpg",
        "/images/stories/book11/story-image-8.jpg",
        "/images/stories/book11/story-image-9.jpg",
        "/images/stories/book11/story-image-10.jpg",
        "/images/stories/book11/story-image-11.jpg",   
        "/images/stories/book11/story-image-12.jpg",
      ]
    },
    {
      title: "Bella's Umbrella",
      description: "Bella's Umbrella is a story about a girl who loves her umbrella and uses it to protect herself from the rain.",
      pages:[
        "/images/stories/book2/story-image-1.jpg",
        "/images/stories/book2/story-image-2.jpg",
        "/images/stories/book2/story-image-3.jpg",
        "/images/stories/book2/story-image-4.jpg",
        "/images/stories/book2/story-image-5.jpg",
        "/images/stories/book2/story-image-6.jpg",
        "/images/stories/book2/story-image-7.jpg",
        "/images/stories/book2/story-image-8.jpg",
        "/images/stories/book2/story-image-9.jpg",
        "/images/stories/book2/story-image-10.jpg",
        "/images/stories/book2/story-image-11.jpg",   
        "/images/stories/book2/story-image-12.jpg",
      ]
    },
    {
      title: "Dora's Jungle Adventure",
      description: "Dora's Jungle Adventure is a story about a girl who goes on an adventure to the jungle and meets a new friend.",
      pages: [
        "/images/stories/book3/story-image-1.jpg",
        "/images/stories/book3/story-image-2.jpg",
        "/images/stories/book3/story-image-3.jpg",
        "/images/stories/book3/story-image-4.jpg",
        "/images/stories/book3/story-image-5.jpg",
        "/images/stories/book3/story-image-6.jpg",
        "/images/stories/book3/story-image-7.jpg",
        "/images/stories/book3/story-image-8.jpg",
        "/images/stories/book3/story-image-9.jpg",
        "/images/stories/book3/story-image-10.jpg",
        "/images/stories/book3/story-image-11.jpg",   
        "/images/stories/book3/story-image-12.jpg",
        "/images/stories/book3/story-image-13.jpg",
      ]
    },
    {
      title: "Bunny's Brave Adventure",
      description: "Bunny's Brave Adventure is a story about a little bunny who goes on an adventure to the jungle and meets a new friend.",
      pages: [
        "/images/stories/book4/story-image-1.jpg",
        "/images/stories/book4/story-image-2.jpg",
        "/images/stories/book4/story-image-3.jpg",
        "/images/stories/book4/story-image-4.jpg",
        "/images/stories/book4/story-image-5.jpg",
        "/images/stories/book4/story-image-6.jpg",
        "/images/stories/book4/story-image-7.jpg",
        "/images/stories/book4/story-image-8.jpg",
        "/images/stories/book4/story-image-9.jpg",
        "/images/stories/book4/story-image-10.jpg",
        "/images/stories/book4/story-image-11.jpg",   
        "/images/stories/book4/story-image-12.jpg",
        "/images/stories/book4/story-image-13.jpg",
      ]
    },
    {
      title: "Om Nom Adventure",
      description: "Om Nom Adventure is a story about a om nom who goes on an adventure and meets a new friend.",
      pages: [
        "/images/stories/book10/story-image-1.jpg",
        "/images/stories/book10/story-image-2.jpg",
        "/images/stories/book10/story-image-3.jpg",
        "/images/stories/book10/story-image-4.jpg",
        "/images/stories/book10/story-image-5.jpg",
        "/images/stories/book10/story-image-6.jpg",
        "/images/stories/book10/story-image-7.jpg",
        "/images/stories/book10/story-image-8.jpg",
        "/images/stories/book10/story-image-9.jpg",
        "/images/stories/book10/story-image-10.jpg",
        "/images/stories/book10/story-image-11.jpg",   
        "/images/stories/book10/story-image-12.jpg",
      ]
    },
    {
      title: "Panda's Big Adventure",
      description: "Panda's Big Adventure is a story about a panda who goes on an adventure to the jungle and meets a new friend.",
      pages: [
        "/images/stories/book5/story-image-1.jpg",
        "/images/stories/book5/story-image-2.jpg",
        "/images/stories/book5/story-image-3.jpg",
        "/images/stories/book5/story-image-4.jpg",
        "/images/stories/book5/story-image-5.jpg",
        "/images/stories/book5/story-image-6.jpg",
        "/images/stories/book5/story-image-7.jpg",
        "/images/stories/book5/story-image-8.jpg",
        "/images/stories/book5/story-image-9.jpg",
        "/images/stories/book5/story-image-10.jpg",
        "/images/stories/book5/story-image-11.jpg",   
        "/images/stories/book5/story-image-12.jpg",
      ]
    },
    {
      title: "Llama Bus Trip",
      description: "Llama Bus Trip is a story about a llama who goes on a bus trip and meets a new friend and enjoys the trip.",
      pages: [
        "/images/stories/book6/story-image-1.jpg",
        "/images/stories/book6/story-image-2.jpg",
        "/images/stories/book6/story-image-3.jpg",
        "/images/stories/book6/story-image-4.jpg",
        "/images/stories/book6/story-image-5.jpg",
        "/images/stories/book6/story-image-6.jpg",
        "/images/stories/book6/story-image-7.jpg",
        "/images/stories/book6/story-image-8.jpg",
        "/images/stories/book6/story-image-9.jpg",
        "/images/stories/book6/story-image-10.jpg",
        "/images/stories/book6/story-image-11.jpg",   
        "/images/stories/book6/story-image-12.jpg",
      ]
    },
    {
      title: "Peppa's Big Adventure",
      description: "Peppa's Big Adventure is a story about a peppa who goes on an adventure and meets a new friend.",
      pages: [
        "/images/stories/book7/story-image-1.jpg",
        "/images/stories/book7/story-image-2.jpg",
        "/images/stories/book7/story-image-3.jpg",
        "/images/stories/book7/story-image-4.jpg",
        "/images/stories/book7/story-image-5.jpg",
        "/images/stories/book7/story-image-6.jpg",
      ]
    },
    {
      title: "Superhero Friends",
      description: "Superhero Friends is a story about a superhero who goes on an adventure and meets a new friend.",
      pages: [
        "/images/stories/book8/story-image-1.jpg",
        "/images/stories/book8/story-image-2.jpg",
        "/images/stories/book8/story-image-3.jpg",
        "/images/stories/book8/story-image-4.jpg",
        "/images/stories/book8/story-image-5.jpg",
        "/images/stories/book8/story-image-6.jpg",
      ]
    },
    {
      title: "The Hulk's Smash Adventure",
      description: "The Hulk's Smash Adventure is a story about a hulk who goes on an adventure and meets a new friend.",
      pages: [
        "/images/stories/book9/story-image-1.jpg",
        "/images/stories/book9/story-image-2.jpg",
        "/images/stories/book9/story-image-3.jpg",
        "/images/stories/book9/story-image-4.jpg",
        "/images/stories/book9/story-image-5.jpg",
        "/images/stories/book9/story-image-6.jpg",
        "/images/stories/book9/story-image-7.jpg",
        "/images/stories/book9/story-image-8.jpg",
        "/images/stories/book9/story-image-9.jpg",
        "/images/stories/book9/story-image-10.jpg",
        "/images/stories/book9/story-image-11.jpg",   
        "/images/stories/book9/story-image-12.jpg",
      ]
    }
  ];
  
  useEffect(() => {
    // Check if we were redirected here due to auth requirement
    if (location.state?.requireAuth) {
      setShowLoginModal(true);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white font-kidBody">
      <Suspense fallback={<div className="w-full h-20 bg-gradient-to-r from-[#FF7F6B] to-[#FFB800]"></div>}>
        <Header storiesRef={storiesRef} />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#FF7F6B] to-[#FFB800] flex items-center justify-center">
        <div className="animate-pulse w-1/2 h-10 bg-white/20 rounded-full"></div>
      </div>}>
        <HeroSection />
      </Suspense>

      {/* Credit Banner */}
      <div className="container mx-auto px-4 py-6">
        <HomeBanner />
      </div>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#4A154B] to-[#6B46C1]"></div>}>
        <QuickStoriesSection kidsImages={QuickStoriesImages} />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#FF7F6B] to-[#FFB800]"></div>}>
        <StorybookSection storyImages={storyBookImages} />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#0F172A] via-[#312E81] to-[#581C87]"></div>}>
        <PhotoBookSection beyondImaginationImages={photoBookImages} />   
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-br from-purple-600 to-indigo-800"></div>}>
        <FableWhispererSection />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6]"></div>}>
        <ReadStoriesSection storyBooks={storyBooks} initialVisibleStories={9} sectionRef={storiesRef} />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#FF7F6B] to-[#FFB800]"></div>}>
        <ListenStoriesSection audioBooks={audioBooks} />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#22C55E] to-[#0EA5E9]"></div>}>
        <CraftedWithCareSection />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-gradient-to-r from-[#22C55E] to-[#0EA5E9]"></div>}>
        <TestimonialsSection testimonials={testimonials} />
      </Suspense>

      <Suspense fallback={<div className="w-full h-64 bg-[#FF7F6B]"></div>}>
        <Footer />
      </Suspense>

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        // After successful login, redirect to the originally requested page
        onLoginSuccess={() => {
          const from = location.state?.from?.pathname || "/app";
          navigate(from);
        }}
      />
    </div>
  );
};

export default Landing; 